/// <amd-module name="Repos/Views/Settings/Repositories-Policies/Components/PolicyPanel" />
import React = require("react");
import { ISettingsViewService, SettingsTabIds } from "Repos/Views/Settings/Common/Services/SettingsViewService";
import { IPolicy, Scope } from "Repos/Views/Settings/Repositories-Policies/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories-Policies/Resources";
import { IObservableArray, IObservableValue, ObservableArray } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Link } from "VSSUI/Link";
import { Panel } from "VSSUI/Panel";
import { Pill, PillSize } from "VSSUI/Pill";
import { ITableColumn, ITableRow, SimpleTableCell, Table, TableColumnLayout } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";

interface IPolicyPanelProps {
    goToProject: () => void;
    goToRef?: (ref: string, repositoryId?: string) => void;
    policy: IPolicy;
    projectName?: string;
    repositoryName?: string;
    show: IObservableValue<boolean>;
}

export class PolicyPanel extends VssComponent<IPolicyPanelProps> {
    private items: IObservableArray<IPolicy>;
    private settingsSvc: ISettingsViewService;

    public constructor(props: IPolicyPanelProps, context: IVssComponentContext) {
        super(props, context);
        this.settingsSvc = context.pageContext.getService<ISettingsViewService>("SettingsViewService");

        this.items = new ObservableArray<IPolicy>();
        if (this.props.policy.inheritedPolicies) {
            this.items.push(...this.props.policy.inheritedPolicies);
        }
        if (this.props.policy.enterprisePolicies) {
            this.items.push(...this.props.policy.enterprisePolicies);
        }
        if (this.props.policy.additonalCurrentScopePolicies) {
            this.items.push(...this.props.policy.additonalCurrentScopePolicies);
        }
    }

    public render = (): JSX.Element => {
        return (
            <ConditionalChildren renderChildren={this.props.show}>
                <Panel
                    onDismiss={() => (this.props.show.value = false)}
                    titleProps={{ text: Resources.PoliciesPanelTitle }}
                    description={this.props.policy.title + Resources.PoliciesPanelDescription}
                >
                    <Table<IPolicy> columns={this.getColumns()} itemProvider={this.items} role="table" onActivate={this.onActivateRow} />
                </Panel>
            </ConditionalChildren>
        );
    };

    private getColumns = (): ITableColumn<{}>[] => {
        return [
            {
                columnLayout: TableColumnLayout.singleLinePrefix,
                id: "configuredOn",
                name: Resources.ConfiguredOn,
                readonly: true,
                renderCell: this.renderCellConfiguredOn,
                width: -50
            },
            {
                id: "appliesTo",
                name: Resources.AppliesTo,
                readonly: true,
                renderCell: this.renderCellAppliesTo,
                width: -50
            },
            {
                id: "readOnly",
                name: "",
                ariaLabel: Resources.ReadOnly,
                readonly: true,
                renderCell: this.renderCellReadOnly,
                width: 95
            }
        ];
    };

    private getConfiguredOnName = (policy: IPolicy) => {
        if (policy.scope !== undefined) {
            if (policy.scope === Scope.Repository) {
                return this.props.repositoryName || "";
            } else {
                return this.props.projectName || "";
            }
        }

        return "";
    };

    private onActivateRow = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<IPolicy>) => {
        this.props.show.value = false;
        this.onClick(undefined, tableRow.data);
    };

    private onClick = (
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement> | undefined,
        data: IPolicy
    ) => {
        if (!event || (!event.shiftKey && !event.ctrlKey)) {
            if (!data.isEnterpriseManaged) {
                if (data.scope === Scope.Project) {
                    this.props.goToProject();
                } else if (data.refName && this.props.goToRef) {
                    this.props.goToRef(data.refName, data.repositoryId);
                }
            }

            if (event) {
                event.preventDefault();
            }
        }
    };

    private renderCellAppliesTo = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IPolicy>, tableItem: IPolicy): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                <div className="flex-column text-ellipsis">
                    <div className="text-ellipsis">
                        <Tooltip text={tableItem.appliesTo}>
                            <span>{tableItem.appliesTo}</span>
                        </Tooltip>
                    </div>
                    <div className="text-ellipsis">
                        <span className="secondary-text body-s">{tableItem.value ? Resources.Active : Resources.Inactive}</span>
                    </div>
                </div>
            </SimpleTableCell>
        );
    };

    private renderCellConfiguredOn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IPolicy>, tableItem: IPolicy): JSX.Element => {
        const colVal = tableItem.confgiuredOn || this.getConfiguredOnName(tableItem);
        let url: string | undefined = undefined;
        if (!tableItem.isEnterpriseManaged) {
            if (tableItem.scope === Scope.Project) {
                url = this.settingsSvc.getUrl(undefined, undefined, SettingsTabIds.policies);
            } else if (tableItem.refName && this.props.goToRef) {
                url = this.settingsSvc.getUrl(tableItem.repositoryId, tableItem.refName, SettingsTabIds.policiesMid);
            }
        }

        const content = url ? (
            <Link className="padding-0" subtle={true} href={url} onClick={event => this.onClick(event, tableItem)}>
                <span>{colVal}</span>
            </Link>
        ) : (
            <span>{colVal}</span>
        );

        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                <div className="flex-column text-ellipsis">
                    <div className="text-ellipsis">
                        <Tooltip text={colVal}>{content}</Tooltip>
                    </div>
                    <div className="text-ellipsis">
                        <span className="secondary-text body-s policy-panel-capitalize">{tableItem.scopeString || " "}</span>
                    </div>
                </div>
            </SimpleTableCell>
        );
    };

    private renderCellReadOnly = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IPolicy>, tableItem: IPolicy): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                <div className="flex-column">
                    {tableItem.isEnterpriseManaged && (
                        <Tooltip text={Resources.ReadOnlyTooltip}>
                            <Pill size={PillSize.compact}>{Resources.ReadOnly}</Pill>
                        </Tooltip>
                    )}
                </div>
            </SimpleTableCell>
        );
    };
}
